// ABOUTME: Runtime .ru/.com resolution for the production/staging domain split.
// ABOUTME: Rewrites canonical *.trainingendurance.com URLs to .ru on a .ru host.

export type Tld = "ru" | "com"

// The canonical base baked into every env file. All "our" service URLs (API,
// auth, avatar, download, chatwoot, store, mcp) are configured against `.com`;
// on a `.ru` host we swap the TLD at runtime so the same build serves both
// domains from a single deployment. Matched only at a domain boundary — the
// `.com` must be followed by a port/path/query/fragment separator or end of
// string — so look-alikes like `trainingendurance.community` are left alone;
// the `g` flag covers URLs that embed the base more than once (redirect target).
const CANONICAL_RE = /trainingendurance\.com(?=[:/?#]|$)/g

/**
 * TLD of the host the app is currently served from. Defaults to `com` for
 * localhost, previews and any non-.ru host, so dev and CI stay on `.com`.
 * A trailing dot (fully-qualified `…ru.`) is normalized away first.
 */
export const runtimeTld = (): Tld => {
  const host = (typeof window !== "undefined" ? window.location.hostname : "").replace(/\.$/, "")
  return host.endsWith(".ru") ? "ru" : "com"
}

/**
 * Whether Western third-party analytics (Google Analytics, Microsoft Clarity,
 * Mixpanel) may run here. The .ru domain is the Russian version of the app,
 * and none of them runs on it: Yandex Metrika does instead (model/metrika),
 * and its errors go to Metric rather than Sentry (model/sentry), both chosen
 * by the same runtimeTld(). Only the host counts, not the visitor's country:
 * `ru.` is a `.com` entry point and keeps the services.
 */
export const foreignServicesAllowed = (): boolean => runtimeTld() !== "ru"

/**
 * Rewrites canonical `*.trainingendurance.com` hosts in a URL to the current
 * runtime TLD. No-op on a `.com` host and for URLs without the canonical base
 * (localhost, external hosts like `lactate.store`), so it is safe to wrap any
 * trusted service URL. Only rewrites the canonical host at a domain boundary —
 * never user-controlled path/query data that merely contains the substring.
 */
export const localizeDomain = (url: string): string => {
  const tld = runtimeTld()
  return tld === "com" ? url : url.replace(CANONICAL_RE, `trainingendurance.${tld}`)
}

/**
 * Resolves a service that lives UNDER THE APP HOST in the single-origin model
 * (GraphQL, Kratos, download constructor). TLD rewriting alone can't follow the
 * manual entry points `eu.`/`ru.trainingendurance.com` — they are `.com` hosts,
 * so `localizeDomain` would leave the canonical `app.` host in place and API
 * traffic would bypass the server the user explicitly chose. Instead, when the
 * configured URL sits on the canonical app host, rebuild it against the CURRENT
 * origin: on `app.`/`eu.`/`ru.`/`app.…ru` alike the request stays same-origin
 * and follows the entry point.
 *
 * Relative URLs (dev proxy paths like `/kratos`) are same-origin already and
 * pass through; URLs on any other host (e.g. dev's cross-origin download
 * constructor) keep the `localizeDomain` fallback.
 */
export const resolveAppServiceUrl = (serviceUrl: string, appUrl: string): string => {
  if (serviceUrl.startsWith("/")) return serviceUrl
  try {
    const service = new URL(serviceUrl)
    if (service.host === new URL(appUrl).host) {
      return window.location.origin + service.pathname + service.search + service.hash
    }
  } catch {
    /* empty or malformed env URL — fall back below */
  }
  return localizeDomain(serviceUrl)
}
